import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, Shield, Clock, ArrowRight, Star } from 'lucide-react';
import { mockData } from '../data/mock';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-white -z-10" />
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-5 -z-5">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iIzAwMCIgb3BhY2l0eT0iLjEiLz48L2c+PC9zdmc+')] bg-repeat" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold">
                <Clock className="w-4 h-4" />
                <span>{mockData.company.availability}</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Expert en <span className="text-blue-600">Lutte Anti-Nuisibles</span> en Alsace
              </h1>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Intervention rapide et efficace contre tous types de nuisibles. 
                Professionnel certifié pour votre tranquillité et votre sécurité.
              </p>

              <div className="flex flex-wrap gap-4">
                <a
                  href={`tel:${mockData.contact.phone.replace(/\s/g, '')}`}
                  className="inline-flex items-center space-x-2 bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 hover:shadow-xl font-semibold group"
                >
                  <Phone className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
                  <span>Appeler Maintenant</span>
                </a>
                
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 bg-white text-blue-600 px-8 py-4 rounded-lg border-2 border-blue-600 hover:bg-blue-50 transition-all duration-300 font-semibold group"
                >
                  <span>Demander un Devis</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-6 pt-4">
                <div className="flex items-center space-x-2 text-gray-700">
                  <Shield className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium">{mockData.company.certification}</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium">Intervention Rapide</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-700">
                  <Star className="w-5 h-5 text-blue-600 fill-blue-600" />
                  <span className="text-sm font-medium">Service de Qualité</span>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-blue-600 rounded-3xl transform rotate-3" />
              <img
                src={mockData.images.hero}
                alt="Professionnel JTS Alsace"
                className="relative rounded-3xl shadow-2xl object-cover w-full h-[500px]"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                <p className="text-gray-900 font-semibold">Disponible 24/7 pour vos urgences</p>
                <p className="text-blue-600 font-bold text-2xl">{mockData.contact.phone}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos Services de Traitement
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Solutions professionnelles adaptées à chaque type de nuisible
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockData.services.map((service) => (
              <Link
                key={service.id}
                to="/services"
                className="group bg-white rounded-xl border-2 border-gray-100 hover:border-blue-500 hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <div className="aspect-w-16 aspect-h-9 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">
                    {service.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    {service.description.substring(0, 80)}...
                  </p>
                  <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:translate-x-2 transition-transform duration-300">
                    <span>En savoir plus</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center space-x-2 bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 font-semibold"
            >
              <span>Voir Tous les Services</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pourquoi Choisir JTS Alsace ?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <Clock className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Disponibilité 24/7
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Nous sommes à votre service jour et nuit, tous les jours de l'année, pour répondre à vos urgences.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Certification Biocide
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Professionnels certifiés utilisant des produits homologués et sécurisés pour votre protection.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <CheckCircle className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Efficacité Garantie
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Traitement professionnel adapté avec suivi pour garantir une élimination complète et durable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Avis de Nos Clients
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {mockData.testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-8 border-2 border-blue-100 hover:border-blue-300 transition-all duration-300"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">"{testimonial.text}"</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.location}</p>
                  </div>
                  <p className="text-xs text-gray-500">{testimonial.date}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Un Problème de Nuisibles ?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            N'attendez pas, contactez-nous maintenant pour une intervention rapide !
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href={`tel:${mockData.contact.phone.replace(/\s/g, '')}`}
              className="inline-flex items-center space-x-2 bg-white text-blue-600 px-8 py-4 rounded-lg hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 font-semibold shadow-xl"
            >
              <Phone className="w-5 h-5" />
              <span>{mockData.contact.phone}</span>
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 bg-blue-800 text-white px-8 py-4 rounded-lg hover:bg-blue-900 transition-all duration-300 transform hover:scale-105 font-semibold"
            >
              <span>Formulaire de Contact</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
